import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, ArrowRight, Star } from 'lucide-react';

const Auth = () => {
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        toast.success('Connexion réussie !');
      } else {
        if (!formData.name.trim()) {
          toast.error('Veuillez entrer votre nom');
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
        toast.success('Compte créé avec succès !');
      }
      navigate(from, { replace: true });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-obsidian pt-32 pb-20 flex items-center justify-center" data-testid="auth-page">
      <div className="w-full max-w-md px-6">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold mb-4">
            <Star className="w-8 h-8 text-obsidian" />
          </div>
          <h1 className="font-syne text-3xl font-bold text-white">
            {mode === 'login' ? 'Connexion' : 'Créer un compte'}
          </h1>
          <p className="text-white/50 mt-2">
            {mode === 'login' 
              ? 'Accédez à votre espace de vote' 
              : 'Rejoignez la communauté Stered an Noz'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {mode === 'register' && (
            <div>
              <label className="block text-sm text-white/70 mb-2">Nom complet</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Votre nom"
                  className="w-full pl-12 pr-4 py-4 bg-obsidian-light border border-white/10 text-white placeholder:text-white/30 focus:border-gold/50 transition-colors"
                  data-testid="name-input"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm text-white/70 mb-2">Adresse email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="vous@exemple.com"
                required
                className="w-full pl-12 pr-4 py-4 bg-obsidian-light border border-white/10 text-white placeholder:text-white/30 focus:border-gold/50 transition-colors"
                data-testid="email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-white/70 mb-2">Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
                required
                minLength={6}
                className="w-full pl-12 pr-4 py-4 bg-obsidian-light border border-white/10 text-white placeholder:text-white/30 focus:border-gold/50 transition-colors"
                data-testid="password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-widest px-6 py-4 text-sm transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                {mode === 'login' ? 'Se connecter' : 'Créer mon compte'}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        {/* Toggle */}
        <div className="mt-8 text-center">
          <p className="text-white/50">
            {mode === 'login' ? "Pas encore de compte ?" : "Déjà un compte ?"}
          </p>
          <button
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="mt-2 text-gold hover:text-gold-glow transition-colors"
            data-testid="toggle-auth-mode"
          >
            {mode === 'login' ? 'Créer un compte' : 'Se connecter'}
          </button>
        </div>
      </div>
    </main>
  );
};

export default Auth;
